import React, { useState, useRef, useEffect } from "react";
import {
  FaUserTie,
  FaUser,
  FaSmile,
  FaBolt,
  FaBriefcase,
  FaCity,
  FaVideo,
  FaShoppingCart,
  FaPen,
  FaCamera,
  FaUserAlt,
  FaBox,
  FaUsers,
  FaGraduationCap,
  FaPlay,
  FaRocket,
  FaStar,
  FaGlobe,
  FaHome,
  FaIndustry,
  FaTree,
  FaBuilding,
  FaCar,
  FaLaptop,
  FaChartLine,
  FaBullhorn,
  FaLaugh,
  FaCloud,
  FaSun,
  FaCloudRain,
  FaSnowflake,
  FaLeaf,
  FaQuestion,
  FaHeart,
  FaChild,
  FaUserFriends,
  FaFilm,
  FaMicrophone,
  FaClock,
  FaExpand,
  FaTag,
  FaPercent,
  FaGift,
  FaTruck,
  FaHourglass,
  FaUserPlus,
  FaBoxes,
  FaCalendarAlt,
  FaPercentage,
  FaTimes,
  FaImage,
  FaCheckCircle,
  FaLanguage,
} from "react-icons/fa";
import {
  MdApartment,
  MdSchool,
  MdSportsEsports,
  MdRestaurant,
  MdLocalHospital,
  MdWbSunny,
} from "react-icons/md";
import { FaUserCircle } from "react-icons/fa";
import apiService from "../../services/api";
import { useBrandigiopsAuth, useBrandigiopsClientProfile } from "../../hooks/useBrandigiopsAuth";
import { fetchClientOnboarding } from "../../utils/brandigiopsAuth";
import { getLogoUrlFromUrl, getCompanyNameFromUrl } from "../../utils/urlParams";

// ── tiny helpers ─────────────────────────────────────────────────────────────

const DropdownField = ({ label, icon, value, options, onChange, highlighted, onManualSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const selected = options.find((o) => o.value === value);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl border hover:shadow-sm transition-all text-base font-medium ${
          highlighted
            ? "border-indigo-400 bg-indigo-50 text-indigo-900 hover:border-indigo-500"
            : "border-gray-200 bg-white text-gray-700 hover:border-primary"
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          <span>{selected?.icon ?? icon}</span>
          <span className="flex flex-col items-start truncate">
            <span
              className="text-xs font-normal leading-none mb-0.5 flex items-center gap-1"
              style={{ color: "#9333ea" }}
            >
              {label}
            </span>
            <span className={`truncate text-sm font-semibold leading-tight ${highlighted ? "text-indigo-900" : "text-gray-700"}`}>
              {selected?.label ?? (
                <span className="text-gray-400 font-normal">Select…</span>
              )}
            </span>
          </span>
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto bg-white rounded-xl border border-gray-200 shadow-lg">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                onManualSelect?.();
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-base hover:bg-gray-50 transition-colors ${value === opt.value ? "bg-blue-50 text-primary font-semibold" : "text-gray-700"}`}
            >
              <span className="flex-shrink-0">{opt.icon}</span>
              <span className="flex-1 text-left">{opt.label}</span>
              {value === opt.value && (
                <svg
                  className="w-4 h-4 text-primary flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MultiSelectDropdown = ({
  label,
  icon,
  selected,
  options,
  onChange,
  forceOpen,
  onForceOpenConsumed,
  highlighted,
  onManualSelect,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  React.useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      onForceOpenConsumed?.();
    }
  }, [forceOpen]);
const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter((v) => v !== val));
    else onChange([...selected, val]);
    onManualSelect?.();
  };
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl border hover:shadow-sm transition-all text-base font-medium ${
          highlighted
            ? "border-indigo-400 bg-indigo-50 text-indigo-900 hover:border-indigo-500"
            : "border-gray-200 bg-white text-gray-700 hover:border-primary"
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          <span>{icon}</span>
          <span className="flex flex-col items-start truncate">
            <span
              className="text-xs font-normal leading-none mb-0.5 flex items-center gap-1"
              style={{ color: "#9333ea" }}
            >
              {label}
              {highlighted && <span className="text-[9px] font-bold text-indigo-600">✨</span>}
            </span>
            <span className={`truncate text-sm font-semibold leading-tight ${highlighted ? "text-indigo-900" : "text-gray-700"}`}>
              {selected.length === 0 ? (
                <span className="text-gray-400 font-normal">Select…</span>
              ) : (
                selected.join(", ")
              )}
            </span>
          </span>
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white rounded-xl border border-gray-200 shadow-lg">
          {options.map((opt) => {
            const isSel = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-base hover:bg-gray-50 transition-colors ${isSel ? "bg-blue-50 text-primary font-semibold" : "text-gray-700"}`}
              >
                <span className="flex-1 text-left">
                  {opt === "I'm not sure" ? "❓" : "🌐"} {opt}
                </span>
                {isSel && (
                  <svg
                    className="w-4 h-4 text-primary"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ImageUploadBox = ({
  label,
  icon,
  preview,
  onUpload,
  onRemove,
  accept = "image/*",
  hint,
  info,
}) => {
  const inputRef = useRef(null);
  return (
    <div>
      {label && (
        <div className="text-base font-semibold text-gray-700 mb-2 flex items-center gap-1">
          <span>{icon}</span> {label}
          {info && (
            <span className="relative group ml-1">
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
              <div className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg p-3 hidden group-hover:block">
                <div className="text-sm text-gray-500">{info.text}</div>
                {info.tip && (
                  <div className="text-sm text-blue-600 font-medium flex items-center gap-1 mt-1">
                    <span>💡</span>
                    <span>{info.tip}</span>
                  </div>
                )}
              </div>
            </span>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onUpload(e.target.files?.[0])}
      />
      <div
        onClick={() => inputRef.current?.click()}
        className="relative border-2 border-dashed border-gray-200 rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary hover:bg-blue-50/30 transition-all min-h-[90px]"
      >
        {preview ? (
          <div className="relative inline-flex">
            {onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md z-10 transition-colors"
              >
                ✕
              </button>
            )}
            <img
              src={preview}
              alt="preview"
              className="max-h-16 rounded-lg object-contain"
            />
          </div>
        ) : (
          <>
            <span className="text-3xl">{icon}</span>
            <span className="text-sm text-gray-500 font-medium text-center">
              {hint ?? `Upload ${label}`}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

// ── ImageTypeCard: combined product/model/background upload card ─────────────

const IMAGE_TYPE_OPTIONS = [
  {
    value: "product",
    label: "Product Image",
    icon: "📦",
    info: {
      text: "Upload your product image and it will appear in the video — the AI will place it naturally within the scene.",
    },
  },
  {
    value: "model",
    label: "Model Image",
    icon: "👤",
    info: {
      text: "Upload a person or model image — the AI will use that exact person as your video presenter.",
    },
  },
  {
    value: "background",
    label: "Background Image",
    icon: "🏞️",
    info: {
      text: "Upload a custom background image — the AI will use it as the scene backdrop for your video.",
    },
  },
];

const ImageTypeCard = ({
  imageUploadType,
  setImageUploadType,
  productPreview,
  modelPreview,
  backgroundPreview,
  handleProductUpload,
  handleModelUpload,
  handleBackgroundUpload,
  setProductPreview,
  setProductUploadedUrl,
  setModelPreview,
  setModelUploadedUrl,
  setBackgroundPreview,
  setBackgroundUploadedUrl,
  onModelWalkingChange,
}) => {
  const [typeOpen, setTypeOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const typeRef = useRef(null);

  const [walkQuestion, setWalkQuestion] = useState(false);
  const [walkingAnswer, setWalkingAnswer] = useState(null);

  React.useEffect(() => {
    const h = (e) => {
      if (typeRef.current && !typeRef.current.contains(e.target))
        setTypeOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const current = IMAGE_TYPE_OPTIONS.find((o) => o.value === imageUploadType);
  const currentPreview =
    imageUploadType === "product"
      ? productPreview
      : imageUploadType === "model"
        ? modelPreview
        : backgroundPreview;
  const currentUpload =
    imageUploadType === "product"
      ? handleProductUpload
      : imageUploadType === "model"
        ? handleModelUpload
        : handleBackgroundUpload;
  const currentRemove =
    imageUploadType === "product"
      ? () => {
          setProductPreview(null);
          setProductUploadedUrl(null);
        }
      : imageUploadType === "model"
        ? () => {
            setModelPreview(null);
            setModelUploadedUrl(null);
          }
        : () => {
            setBackgroundPreview(null);
            setBackgroundUploadedUrl(null);
          };

  const inputRef = useRef(null);

  return (
    <div className="col-span-full">
      {/* Label row with inline type-selector dropdown */}
      {/* Reddit-style pill selector */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <p className="text-base font-semibold text-gray-700 mb-3">
          Choose Other Images:
        </p>
        {IMAGE_TYPE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              setImageUploadType(opt.value);
              setConfirmed(true);
              if (opt.value === "model") {
                setWalkQuestion(true); // show question first
                setConfirmed(false); // hide upload box until answered
              } else {
                setWalkQuestion(false);
                setWalkingAnswer(null);
                onModelWalkingChange?.(false);
              }
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${
              imageUploadType === opt.value && confirmed
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-500"
            }`}
          >
            <span>{opt.icon}</span>
            <span>{opt.label}</span>
          </button>
        ))}
      </div>

      {walkQuestion && walkingAnswer === null && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-xl space-y-2">
          <p className="text-sm font-semibold text-blue-800">
            🚶 Do you want the model to walk around the background premises?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setWalkingAnswer(true);
                setConfirmed(true);
                onModelWalkingChange?.(true);
              }}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => {
                setWalkingAnswer(false);
                setConfirmed(true);
                onModelWalkingChange?.(false);
              }}
              className="px-4 py-1.5 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
            >
              No
            </button>
          </div>
        </div>
      )}

      {/* Upload box — shown only after type is selected */}
      {confirmed && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => currentUpload(e.target.files?.[0])}
          />
          <div
            onClick={() => inputRef.current?.click()}
            className="relative border-2 border-dashed border-gray-200 rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary hover:bg-blue-50/30 transition-all min-h-[90px]"
          >
            {currentPreview ? (
              <div className="relative inline-flex">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    currentRemove();
                  }}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md z-10 transition-colors"
                >
                  ✕
                </button>
                <img
                  src={currentPreview}
                  alt="preview"
                  className="max-h-16 rounded-lg object-contain"
                />
              </div>
            ) : (
              <>
                <span className="text-3xl">{current?.icon}</span>
                <span className="text-sm text-gray-500 font-medium text-center">
                  PNG, JPG • Max 5MB
                </span>
                {imageUploadType === "model" && walkingAnswer === true && (
                  <div className="mt-1 px-2 py-1.5 bg-blue-50 border border-blue-100 rounded-lg">
                    <p className="text-xs text-blue-600 font-semibold text-center">
                      📸 Please upload a full standing pic of the model
                    </p>
                    <p className="text-xs text-blue-500 text-center mt-0.5">
                      Not a headshot — the AI needs the full body to animate
                      walking
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// -- main component --

const VideoGeneratorLayout = ({
  onSubmit,
  isLoading,
  sceneWorkflow,
  clientId,
}) => {
  const [prompt, setPrompt] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [clientProfileLoading, setClientProfileLoading] = useState(false);
  const [clientProfileLoaded, setClientProfileLoaded] = useState(false);
  const [scriptMode, setScriptMode] = useState("ai-enhanced");
  const [duration, setDuration] = useState("8");
  const [showModelConfirmModal, setShowModelConfirmModal] = useState(false);
  const [showProductConfirmModal, setShowProductConfirmModal] = useState(false);
  const [showLogoConfirmModal, setShowLogoConfirmModal] = useState(false);
  const [skipLogo, setSkipLogo] = useState(false);
  const [skipProduct, setSkipProduct] = useState(false);
  const [skipModel, setSkipModel] = useState(false);
  const [showNoImageModal, setShowNoImageModal] = useState(false);

  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [videoType, setVideoType] = useState("promotional");
  const [presenter, setPresenter] = useState("male");
  const [familyOrigin, setFamilyOrigin] = useState("");
  const [styleTone, setStyleTone] = useState("professional");
  const [dialogue, setDialogue] = useState("yes");
  const [background, setBackground] = useState("");
  const [season, setSeason] = useState("");
  const [cameraShot, setCameraShot] = useState("medium");
  const [cameraMotion, setCameraMotion] = useState("");
  const [industry, setIndustry] = useState("");
  const [offer, setOffer] = useState("");
  const [logoPosition, setLogoPosition] = useState("");
  const [languages, setLanguages] = useState([]);
  const [accent, setAccent] = useState("");

  useEffect(() => {
    if (dialogue === "no") {
      setLanguages([]);
      setAccent("");
    }
  }, [dialogue]);

  const [logoPreview, setLogoPreview] = useState(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState(null);
  const [referenceVideoUrl, setReferenceVideoUrl] = useState(null);
  const [referenceVideoPreview, setReferenceVideoPreview] = useState(null);
  const [referenceVideoLink, setReferenceVideoLink] = useState("");
  const [productPreviews, setProductPreviews] = useState([]);
  const [modelPreviews, setModelPreviews] = useState([]);
  const [backgroundPreviews, setBackgroundPreviews] = useState([]);
  const [logoUploadedUrl, setLogoUploadedUrl] = useState(null);
  const [referenceImageUrl, setReferenceImageUrl] = useState(null);
  const [productUploadedUrls, setProductUploadedUrls] = useState([]);
  const [modelUploadedUrls, setModelUploadedUrls] = useState([]);
  const [backgroundUploadedUrls, setBackgroundUploadedUrls] = useState([]);
  const [imageUploadType, setImageUploadType] = useState("product");

  // total subjects uploaded across all types (max 3)
  const totalSubjects =
    productUploadedUrls.length +
    modelUploadedUrls.length +
    backgroundUploadedUrls.length +
    (logoUploadedUrl ? 1 : 0);
  const [voicePreview, setVoicePreview] = useState(null);
  const [voiceFile, setVoiceFile] = useState(null);
  const [voiceUploadedUrl, setVoiceUploadedUrl] = useState(null);
  const [uploadingAssets, setUploadingAssets] = useState({
    logo: false,
    referenceImage: false,
    referenceVideo: false,
    product: false,
    model: false,
    background: false,
  });

  const setAssetUploading = (key, value) => {
    setUploadingAssets((prev) => ({ ...prev, [key]: value }));
  };

  const [showLipSyncPopup, setShowLipSyncPopup] = useState(false);
  const [lipSyncPopupShown, setLipSyncPopupShown] = useState(false);
  const [openLanguageDropdown, setOpenLanguageDropdown] = useState(false);
  const [showIdeasModal, setShowIdeasModal] = useState(false);
const [ideas, setIdeas] = useState([]);
const [isLoadingIdeas, setIsLoadingIdeas] = useState(false);
const [ideasError, setIdeasError] = useState("");
const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
const [generateIdeasError, setGenerateIdeasError] = useState("");
const [selectedIdeaId, setSelectedIdeaId] = useState(null); // tracks idea picked, so we can mark it "published" after successful generation
const [ideaAppliedFields, setIdeaAppliedFields] = useState(new Set()); // tracks which fields were auto-filled from an idea, for highlighting

const clearIdeaHighlight = (field) => {
  setIdeaAppliedFields((prev) => {
    if (!prev.has(field)) return prev;
    const next = new Set(prev);
    next.delete(field);
    return next;
  });
};
  const [error, setError] = useState("");
  const canSubmit = prompt.trim().length > 0 && duration;

  const [showWalkingModal, setShowWalkingModal] = useState(false);
  const [pendingModelFile, setPendingModelFile] = useState(null);
  const [fileUploadError, setFileUploadError] = useState("");
  const [modelWalking, setModelWalking] = useState(false);
  const [showWalkingInlineMsg, setShowWalkingInlineMsg] = useState(false);
  const [walkingInlineAnswer, setWalkingInlineAnswer] = useState(null);
  const [aiModel, setAiModel] = useState("veo");
  const [generateThumbnails, setGenerateThumbnails] = useState(false);
  const [hookLine, setHookLine] = useState("");
  const [hasGenerated, setHasGenerated] = useState(false);
  const urlClientId = new URLSearchParams(window.location.search).get("clientId");
  const effectiveClientId =clientId || urlClientId;
  const authToken = useBrandigiopsAuth();
  const parentProfile = useBrandigiopsClientProfile(effectiveClientId);

  const applyClientProfile = (name, logo) => {
    const urlCompanyName = getCompanyNameFromUrl();
    if (name && !urlCompanyName) setCompanyName(name);
    const urlLogo = getLogoUrlFromUrl();
    if (!urlLogo && logo) {
      setLogoUploadedUrl(logo);
      setLogoPreview(logo);
    }
    if (name || logo || urlLogo || urlCompanyName) setClientProfileLoaded(true);
  };

  // companyName / logoUrl passed directly in URL
  useEffect(() => {
    const urlLogo = getLogoUrlFromUrl();
    const urlCompanyName = getCompanyNameFromUrl();

    if (urlLogo) {
      setLogoUploadedUrl(urlLogo);
      setLogoPreview(urlLogo);
    }
    if (urlCompanyName) {
      setCompanyName(urlCompanyName);
    }
    if (urlLogo || urlCompanyName) setClientProfileLoaded(true);
  }, []);

  // Profile sent from Brandigiops parent via postMessage
  useEffect(() => {
    if (!parentProfile) return;
    applyClientProfile(parentProfile.companyName, parentProfile.logoUrl);
  }, [parentProfile]);

  // Fetch from client onboarding API — always when clientId (logo in URL must not skip this)
  useEffect(() => {
    if (!effectiveClientId) return;

    let cancelled = false;

    const loadProfile = async () => {
      setClientProfileLoading(true);
      try {
        const profile = await fetchClientOnboarding(effectiveClientId, authToken);
        if (cancelled) return;
        applyClientProfile(profile.companyName, profile.logoUrl);
      } catch (e) {
        if (!cancelled) console.error("Failed to fetch client profile:", e);
      } finally {
        if (!cancelled) setClientProfileLoading(false);
      }
    };

    loadProfile();
  }, [effectiveClientId, authToken]);

  // Auto-fill from a subtask idea when redirected in with a plan-taskid in the URL
  useEffect(() => {
    const subTaskId = new URLSearchParams(window.location.search).get(
      "plan-taskid",
    );
    if (!subTaskId || !effectiveClientId) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `https://ideas-uat.brandigiops.com/ideas/by-subtask/${encodeURIComponent(
            subTaskId,
          )}?clientId=${encodeURIComponent(effectiveClientId)}`,
        );
        if (!response.ok) {
          if (response.status === 404) return;
          throw new Error(`Failed to load idea data for subtask ${subTaskId}`);
        }

        const idea = await response.json();
        if (cancelled || !idea || !idea._id) return;

        applyIdea(idea);
      } catch (err) {
        console.warn("Video subtask prefill failed", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveClientId]);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    setAssetUploading("logo", true);
    try {
      const res = await apiService.uploadLogo(file);
      setLogoUploadedUrl(res.url);
    } catch (e) {
      console.error("Logo upload failed:", e);
    } finally {
      setAssetUploading("logo", false);
    }
  };
  const handleReferenceImageUpload = async (file) => {
    if (!file) return;
    setReferenceImagePreview(URL.createObjectURL(file));
    setAssetUploading("referenceImage", true);
    try {
      const res = await apiService.uploadReferenceImage(file);
      setReferenceImageUrl(res.url);
    } catch (e) {
      console.error("Reference image upload failed:", e);
      setError("Reference image upload failed. Please try again.");
    } finally {
      setAssetUploading("referenceImage", false);
    }
  };

  const handleReferenceVideoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setError("Please upload a valid video file.");
      return;
    }
    setReferenceVideoPreview(URL.createObjectURL(file));
    setAssetUploading("referenceVideo", true);
    try {
      const res = await apiService.uploadReferenceVideo(file);
      setReferenceVideoUrl(res.url);
      setError("");
    } catch (e) {
      console.error("Reference video upload failed:", e);
      setError("Reference video upload failed. Please try again.");
    } finally {
      setAssetUploading("referenceVideo", false);
    }
  };
  const isSameFile = (fileA, fileB) =>
    fileA && fileB && fileA.name === fileB.name && fileA.size === fileB.size;

  const [lastProductFile, setLastProductFile] = useState(null);
  const [lastModelFile, setLastModelFile] = useState(null);
  const [lastBackgroundFile, setLastBackgroundFile] = useState(null);

  const handleProductUpload = async (file) => {
    if (!file || totalSubjects >= 3) return;
    if (isSameFile(file, lastModelFile)) {
      setError("Product image cannot be the same as the model image.");
      return;
    }
    if (isSameFile(file, lastBackgroundFile)) {
      setError("Product image cannot be the same as the background image.");
      return;
    }
    setLastProductFile(file);
    const preview = URL.createObjectURL(file);
    setProductPreviews((p) => [...p, preview]);
    setAssetUploading("product", true);
    try {
      const res = await apiService.uploadProductImage(file);
      setProductUploadedUrls((p) => [...p, res.url]);
    } catch (e) {
      console.error("Product upload failed:", e);
    } finally {
      setAssetUploading("product", false);
    }
  };
  const handleModelUpload = async (file) => {
    if (!file || totalSubjects >= 3) return;
    if (isSameFile(file, lastProductFile)) {
      setError("Model image cannot be the same as the product image.");
      return;
    }
    if (isSameFile(file, lastBackgroundFile)) {
      setError("Model image cannot be the same as the background image.");
      return;
    }
    setLastModelFile(file);
    const preview = URL.createObjectURL(file);
    setModelPreviews((p) => [...p, preview]);
    setAssetUploading("model", true);
    try {
      const res = await apiService.uploadModelImage(file);
      setModelUploadedUrls((p) => [...p, res.url]);
    } catch (e) {
      console.error("Model upload failed:", e);
    } finally {
      setAssetUploading("model", false);
    }
  };
  const handleBackgroundUpload = async (file) => {
    if (!file || totalSubjects >= 3) return;
    if (isSameFile(file, lastProductFile)) {
      setError("Background image cannot be the same as the product image.");
      return;
    }
    if (isSameFile(file, lastModelFile)) {
      setError("Background image cannot be the same as the model image.");
      return;
    }
    setLastBackgroundFile(file);
    const preview = URL.createObjectURL(file);
    setBackgroundPreviews((p) => [...p, preview]);
    setAssetUploading("background", true);
    try {
      const res = await apiService.uploadBackgroundImage(file);
      setBackgroundUploadedUrls((p) => [...p, res.url]);
    } catch (e) {
      console.error("Background upload failed:", e);
    } finally {
      setAssetUploading("background", false);
    }
  };
  const proceedWithGenerate = () => {
    setShowModelConfirmModal(false);
    runGenerate();
  };

const handleGenerate = () => {
  if (!prompt.trim()) {
    setError("Please enter a video description.");
    return;
  }
  if (!duration) {
    setError("Please select a duration.");
    return;
  }
  const hasAnyImage =
    !!referenceImageUrl ||
    productUploadedUrls.length > 0 ||
    modelUploadedUrls.length > 0 ||
    backgroundUploadedUrls.length > 0 ||
    !!logoUploadedUrl;
  setError("");

  if (referenceImageUrl) {
    runGenerate();
    return;
  }

  if (!hasAnyImage) {
    runGenerate();
    return;
  }

    // Check logo first
    if (!logoUploadedUrl && !skipLogo) {
      setShowLogoConfirmModal(true);
      return;
    }
    // Then product
    if (productUploadedUrls.length === 0 && !skipProduct) {
      setShowProductConfirmModal(true);
      return;
    }
    // Then model
    if (modelUploadedUrls.length === 0 && !skipModel) {
      setShowModelConfirmModal(true);
      return;
    }
    runGenerate();
  };

const fetchIdeas = async () => {
  setShowIdeasModal(true);
  setIsLoadingIdeas(true);
  setIdeasError("");
  if (!effectiveClientId) {
    setIdeasError("No client ID found. Please add ?clientId=YOUR_ID to the URL.");
    setIsLoadingIdeas(false);
    return;
  }
  try {
    const response = await fetch(
      `https://ideas-uat.brandigiops.com/ideas/${effectiveClientId}?contentType=video`
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    setIdeas(Array.isArray(data) ? data : data.data || []);
  } catch (err) {
    setIdeasError(err instanceof Error ? err.message : "Failed to load ideas");
  } finally {
    setIsLoadingIdeas(false);
  }
};

const generateNewIdeas = async () => {
  setIsGeneratingIdeas(true);
  setGenerateIdeasError("");
  try {
    // Step 1: kick off generation job
    const genRes = await fetch(`https://ideas-uat.brandigiops.com/ideas/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId: effectiveClientId,
        totalTarget: 10,
        mix: { video: 10},
      }),
    });
    if (!genRes.ok) throw new Error(`Failed to start idea generation (status ${genRes.status})`);
    const genData = await genRes.json();
    const jobId = genData.jobId;
    if (!jobId) throw new Error("No jobId returned from generate call");

    // Step 2: process the queued job
    const processRes = await fetch(`https://ideas-uat.brandigiops.com/ideas/process/${jobId}`, {
      method: "POST",
    });
    if (!processRes.ok) throw new Error(`Failed to process idea generation job (status ${processRes.status})`);

    // Step 3: refresh the ideas list now that new ones exist
    await fetchIdeas();
  } catch (err) {
    setGenerateIdeasError(err instanceof Error ? err.message : "Failed to generate new ideas");
  } finally {
    setIsGeneratingIdeas(false);
  }
};

const applyIdea = (idea) => {
  console.log("🎬 Applying video idea:", idea);
  setOffer("");

  const vs = idea?.videoSettings || {};
  const appliedFields = new Set();

  const applyField = (value, setter, field) => {
    if (value !== undefined && value !== null && value !== "") {
      setter(String(value));
      appliedFields.add(field);
    }
  };

  applyField(vs.videoType || "promotional", setVideoType, "videoType");
  applyField(vs.aspectRatio || "16:9", setAspectRatio, "aspectRatio");
  applyField(vs.presenter || "male", setPresenter, "presenter");
  applyField(vs.familyOrigin || "indian", setFamilyOrigin, "familyOrigin");
  applyField(vs.styleTone || "professional", setStyleTone, "styleTone");
  applyField(vs.dialogue || "yes", setDialogue, "dialogue");
  applyField(vs.background || "modern-office", setBackground, "background");
  applyField(vs.season || "not-sure", setSeason, "season");
  applyField(vs.cameraShot || "medium", setCameraShot, "cameraShot");
  applyField(vs.cameraMotion || "not-sure", setCameraMotion, "cameraMotion");
  applyField(vs.industry || "technology", setIndustry, "industry");
  applyField(vs.logoPosition || "top-left", setLogoPosition, "logoPosition");
  applyField(vs.accent || "not-sure", setAccent, "accent");
  applyField(vs.scriptMode || "ai-enhanced", setScriptMode, "scriptMode");
  applyField(vs.duration || "16", setDuration, "duration");
  applyField(vs.aiModel || "veo", setAiModel, "aiModel");

  const selectedLanguages =
    Array.isArray(vs.languages) && vs.languages.length > 0
      ? vs.languages
      : ["English"];

  setLanguages(selectedLanguages);
  appliedFields.add("languages");

  const promptParts = [
    idea.visualConcept,
    idea.aiVideoPrompt,
    idea.storyline,
  ].filter(Boolean);

  if (promptParts.length > 0) {
    setPrompt(promptParts.join(" "));
    appliedFields.add("prompt");
  }

  setIdeaAppliedFields(appliedFields);
  setSelectedIdeaId(idea._id || idea.id || null);
  setShowIdeasModal(false);

  console.log("✅ Applied video settings:", {
    ...vs,
    languages: selectedLanguages,
  });
};

  // Resolve short codes → full display labels
  const runGenerate = () => {
    const offerLabels = {
      "50-off": "50% Off",
      bogo: "Buy One Get One",
      "free-shipping": "Free Shipping",
      "limited-time": "Limited Time",
      "new-customer": "New Customer",
      "bundle-deal": "Bundle Deal",
      "seasonal-sale": "Seasonal Sale",
      clearance: "Clearance",
      "no-offer": "No Offer",
      "not-sure": "I'm not sure",
    };
    const logoLabels = {
      "top-left": "Top Left Corner",
      "top-right": "Top Right Corner",
      "bottom-left": "Bottom Left Corner",
      "bottom-right": "Bottom Right Corner",
      center: "Center",
      "no-logo": "No Logo",
      "not-sure": "I'm not sure",
    };
    const presenterLabels = {
      male: "Male",
      female: "Female",
      "not-sure": "I'm not sure",
    };
    const familyOriginLabels = {
      indian: "Indian",
      "east-asian": "East Asian",
      "southeast-asian": "Southeast Asian",
      "middle-eastern": "Middle Eastern",
      african: "African",
      caucasian: "Caucasian / European",
      "latin-american": "Latin American / Hispanic",
      "north-american": "North American",
      "pacific-islander": "Pacific Islander / Oceanian",
      "not-sure": "I'm not sure",
    };
    const styleToneLabels = {
      professional: "Professional",
      casual: "Casual",
      energetic: "Energetic",
      luxury: "Luxury",
      friendly: "Friendly",
      formal: "Formal",
      confident: "Confident",
      enthusiastic: "Enthusiastic",
      "not-sure": "I'm not sure",
    };
    const backgroundLabels = {
      "modern-office": "Modern Office",
      studio: "Studio",
      outdoor: "Outdoor",
      home: "Home",
      nature: "Nature",
      urban: "Urban",
      "not-sure": "I'm not sure",
    };
    const seasonLabels = {
      sunny: "Sunny",
      cloudy: "Cloudy",
      rainy: "Rainy",
      snowy: "Snowy",
      autumn: "Autumn",
      spring: "Spring",
      summer: "Summer",
      winter: "Winter",
      "not-sure": "I'm not sure",
    };
    const cameraShotLabels = {
      "close-up": "Close-up",
      medium: "Medium",
      "full-body": "Full Body",
      "wide-shot": "Wide Shot",
      "not-sure": "I'm not sure",
    };
    const cameraMotionLabels = {
      static: "Static",
      "pan-left": "Pan Left",
      "pan-right": "Pan Right",
      "zoom-in": "Zoom In",
      "zoom-out": "Zoom Out",
      "tilt-up": "Tilt Up",
      "tilt-down": "Tilt Down",
      dynamic: "Dynamic",
      "not-sure": "I'm not sure",
    };
    const industryLabels = {
      technology: "Technology",
      healthcare: "Healthcare",
      education: "Education",
      finance: "Finance",
      retail: "Retail",
      "real-estate": "Real Estate",
      automotive: "Automotive",
      "food-beverage": "Food & Beverage",
      entertainment: "Entertainment",
      fitness: "Fitness",
      travel: "Travel",
      fashion: "Fashion",
      "not-sure": "I'm not sure",
    };

    const resolvedPresenter = presenterLabels[presenter] || presenter;
    const resolvedFamilyOrigin =
      familyOriginLabels[familyOrigin] || familyOrigin;
    const resolvedStyleTone = styleToneLabels[styleTone] || styleTone;
    const resolvedBackground = backgroundLabels[background] || background;
    const resolvedSeason = seasonLabels[season] || season;
    const resolvedCameraShot = cameraShotLabels[cameraShot] || cameraShot;
    const resolvedCameraMotion =
      cameraMotionLabels[cameraMotion] || cameraMotion;
    const resolvedIndustry = industryLabels[industry] || industry;
    const resolvedOffer = offerLabels[offer] || offer;
    const resolvedLogoPosition = logoLabels[logoPosition] || logoPosition;

    // Build Video Specifications block
    const specifications = [];
    if (
      (dialogue === "yes" || modelUploadedUrls.length > 0) &&
      resolvedPresenter &&
      resolvedPresenter !== "I'm not sure"
    )
      specifications.push(`Presenter: ${resolvedPresenter}`);
    if (resolvedFamilyOrigin && resolvedFamilyOrigin !== "I'm not sure")
      specifications.push(`Family Origin: ${resolvedFamilyOrigin}`);
    if (resolvedStyleTone && resolvedStyleTone !== "I'm not sure")
      specifications.push(`Style & Tone: ${resolvedStyleTone}`);
    if (
      resolvedBackground &&
      resolvedBackground !== "I'm not sure" &&
      totalSubjects === 0
    )
      specifications.push(`Background: ${resolvedBackground}`);
    if (resolvedSeason && resolvedSeason !== "I'm not sure")
      specifications.push(`Season/Weather: ${resolvedSeason}`);
    if (resolvedCameraShot && resolvedCameraShot !== "I'm not sure")
      specifications.push(`Camera Shot: ${resolvedCameraShot}`);
    if (resolvedCameraMotion && resolvedCameraMotion !== "I'm not sure")
      specifications.push(`Camera Motion: ${resolvedCameraMotion}`);
    if (resolvedIndustry && resolvedIndustry !== "I'm not sure")
      specifications.push(`Industry: ${resolvedIndustry}`);
    if (
      resolvedOffer &&
      resolvedOffer !== "I'm not sure" &&
      resolvedOffer !== "No Offer"
    )
      specifications.push(`Offers: ${resolvedOffer}`);
    if (
      resolvedLogoPosition &&
      resolvedLogoPosition !== "I'm not sure" &&
      resolvedLogoPosition !== "No Logo"
    )
      specifications.push(`Logo Position: ${resolvedLogoPosition}`);
    if (dialogue === "yes" && languages.length > 0)
      specifications.push(`Language: ${languages.join(", ")}`);
    if (dialogue === "yes" && accent && accent !== "not-sure")
      specifications.push(`Voice Accent: ${accent}`);
    if (modelUploadedUrls.length > 0 && modelWalking && dialogue === "yes")
      specifications.push(
        `Model Motion:Full-body walking sequence with realistic movement. The presenter should walk naturally across the scene, interact with the environment, and maintain eye contact with the camera while speaking.`,
      );

    if (prompt.trim())
      specifications.push(`Additional Text Prompt: ${prompt.trim()}`);

    const fullPrompt =
      specifications.length > 0
        ? `${prompt.trim()}\n\nVideo Specifications:\n${specifications.join("\n")}`
        : prompt.trim();

if (onSubmit) {
  setHasGenerated(true);
  Promise.resolve(
    onSubmit({
      prompt: fullPrompt,
      topic: prompt.trim(),
      companyName,
      scriptMode,
      duration,
      aspectRatio,
      videoType,
      dialogue,
      offer: resolvedOffer,
      logoPosition: resolvedLogoPosition,
      languages,
      referenceImageUrl,
      logoUrl: logoUploadedUrl,
      referenceVideoUrl,
      productImgUrls: productUploadedUrls,
      modelImgUrls: modelUploadedUrls,
      backgroundUrls: backgroundUploadedUrls,
      aiModel,
      generateThumbnails,
      voiceUrl: dialogue === "yes" ? voiceUploadedUrl : null,
      hookLine: hookLine.trim() || undefined,
    })
  ).catch((err) => {
    console.error("Video generation failed:", err);
  });
}
  };

  const durationOptions =
    aiModel === "seedance"
      ? [
          { value: "5", label: "5 seconds", icon: "⏱️" },
          { value: "6", label: "6 seconds", icon: "⏱️" },
          { value: "7", label: "7 seconds", icon: "⏱️" },
          { value: "8", label: "8 seconds", icon: "⏱️" },
          { value: "9", label: "9 seconds", icon: "⏱️" },
          { value: "10", label: "10 seconds", icon: "⏱️" },
          { value: "11", label: "11 seconds", icon: "⏱️" },
          { value: "12", label: "12 seconds", icon: "⏱️" },
          { value: "13", label: "13 seconds", icon: "⏱️" },
          { value: "14", label: "14 seconds", icon: "⏱️" },
          { value: "15", label: "15 seconds", icon: "⏱️" },
        ]
      : scriptMode === "direct"
      ? [
          { value: "4", label: "4 seconds", icon: "⏱️" },
          { value: "6", label: "6 seconds", icon: "⏱️" },
          { value: "8", label: "8 seconds", icon: "⏱️" },
        ]
      : [
          { value: "8", label: "8 seconds", icon: "⏱️" },
          { value: "16", label: "16 seconds", icon: "⏱️" },
          { value: "24", label: "24 seconds", icon: "⏱️" },
          { value: "32", label: "32 seconds", icon: "⏱️" },
          { value: "40", label: "40 seconds", icon: "⏱️" },
          { value: "48", label: "48 seconds", icon: "⏱️" },
        ];

  return (
    <div className="flex flex-col lg:flex-row gap-3 w-full px-1">
      {/* ── LEFT CONFIG PANEL ── */}
      <div className={`min-w-0 space-y-2 ${hasGenerated ? "w-full lg:w-[65%]" : "w-full"}`}>
        {/* Describe */}
        <div className="bg-pink-50 rounded-2xl border border-gray-200 p-2 space-y-1.5">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={fetchIdeas}
              className="flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-full hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm whitespace-nowrap"
            >
              <span>💡</span>
              <span>Show Ideas</span>
            </button>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              clearIdeaHighlight("prompt");
            }}
            placeholder="✨ Describe your video vision ... (e.g., 'A 30-second promotional video for a healthcare brand with a professional Indian presenter')"
            rows={3}
            className={`w-full resize-none rounded-xl border px-3 py-2.5 text-base placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
              ideaAppliedFields.has("prompt")
                ? "border-indigo-400 bg-indigo-50 text-indigo-900 focus:ring-indigo-200 focus:border-indigo-500"
                : "border-gray-200 text-gray-700 focus:ring-primary/30 focus:border-primary"
            }`}
          />
          <div className="flex items-center justify-end gap-2 text-sm text-gray-400">
            {[
              {
                value: "seedance",
                label: "⚡ Seedance",
                badge: "Fast",
                badgeClass: "bg-green-100 text-green-700 border-green-200",
                activeClass: "border-green-400 bg-green-50 text-green-800",
              },
              {
                value: "veo",
                label: "🎬 Veo",
                badge: "Premium",
                badgeClass: "bg-purple-100 text-purple-700 border-purple-200",
                activeClass: "border-purple-400 bg-purple-50 text-purple-800",
              },
            ].map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => {
                  setAiModel(m.value);
                  if (m.value === "seedance") {
                    setDuration("5");
                  } else {
                    setDuration(scriptMode === "direct" ? "4" : "16");
                  }
                }}
                className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-semibold transition-all ${
                  aiModel === m.value
                    ? m.activeClass
                    : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                }`}
              >
                {m.label}
                <span className={`text-[9px] px-1 py-0.5 rounded-full border font-bold ${m.badgeClass}`}>
                  {m.badge}
                </span>
              </button>
            ))}
            <span>Ratio: {aspectRatio}</span>
          </div>
        </div>

        {/* ── CORE SETTINGS ── */}
        <div className="bg-pink-50 rounded-2xl border border-pink-100 p-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
<DropdownField
              label="Script Mode"
              icon="⚡"
              value={scriptMode}
              onChange={(v) => {
                setScriptMode(v);
                setDuration(v === "direct" ? "4" : "16");
              }}
              options={[
                { value: "ai-enhanced", label: "AI Enhanced", icon: "🚀" },
                { value: "direct", label: "Direct", icon: "⚡" },
              ]}
              highlighted={ideaAppliedFields.has("scriptMode")}
              onManualSelect={() => clearIdeaHighlight("scriptMode")}
            />
            <DropdownField
              label="Duration"
              icon="⏱️"
              value={duration}
              onChange={setDuration}
              options={durationOptions}
              highlighted={ideaAppliedFields.has("duration")}
              onManualSelect={() => clearIdeaHighlight("duration")}
            />
            <DropdownField
              label="Aspect Ratio"
              icon="📐"
              value={aspectRatio}
              onChange={setAspectRatio}
              options={[
                { value: "16:9", label: "Landscape (16:9)", icon: "📺" },
                { value: "9:16", label: "Portrait (9:16)", icon: "📱" },
              ]}
              highlighted={ideaAppliedFields.has("aspectRatio")}
              onManualSelect={() => clearIdeaHighlight("aspectRatio")}
            />
            <DropdownField
              label="Video Type"
              icon="🎬"
              value={videoType}
              onChange={setVideoType}
              options={[
                { value: "promotional", label: "Promotional", icon: "📢" },
                { value: "educational", label: "Educational", icon: "🎓" },
                { value: "tutorial", label: "Tutorial", icon: "▶️" },
                { value: "product-demo", label: "Product Demo", icon: "📦" },
                { value: "testimonial", label: "Testimonial", icon: "⭐" },
                { value: "marketing", label: "Marketing", icon: "📈" },
                { value: "entertainment", label: "Entertainment", icon: "😄" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
              highlighted={ideaAppliedFields.has("videoType")}
              onManualSelect={() => clearIdeaHighlight("videoType")}
            />
            <DropdownField
              label="Dialogue"
              icon="🎙️"
              value={dialogue}
              onChange={(value) => {
                setDialogue(value);
                if (value === "no") {
                  setLanguages([]);
                  setAccent("");
                }
              }}
              options={[
                { value: "yes", label: "Yes", icon: "🎙️" },
                { value: "no", label: "No", icon: "🔇" },
              ]}
              highlighted={ideaAppliedFields.has("dialogue")}
              onManualSelect={() => clearIdeaHighlight("dialogue")}
            />
            <p className="col-span-2 sm:col-span-3 text-xs text-gray-500 -mt-1">
              {dialogue === "yes"
                ? "Yes: the video will include spoken dialogue and voice."
                : "No: the video will use visuals only without speech or voice-over."}
            </p>
            <DropdownField
              label="Style & Tone"
              icon="🎭"
              value={styleTone}
              onChange={setStyleTone}
              options={[
                { value: "professional", label: "Professional", icon: "💼" },
                { value: "friendly", label: "Friendly", icon: "😊" },
                { value: "confident", label: "Confident", icon: "⚡" },
                { value: "enthusiastic", label: "Enthusiastic", icon: "🚀" },
                { value: "casual", label: "Casual", icon: "👤" },
                { value: "formal", label: "Formal", icon: "🤵" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
              highlighted={ideaAppliedFields.has("styleTone")}
              onManualSelect={() => clearIdeaHighlight("styleTone")}
            />
          </div>
        </div>

        {/* ── PRESENTER ── */}
        <div className="bg-pink-50 rounded-2xl border border-pink-100 p-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
<DropdownField
              label="Presenter"
              icon="👤"
              value={presenter}
              onChange={setPresenter}
              options={[
                { value: "male", label: "Male", icon: "👨‍💼" },
                { value: "female", label: "Female", icon: "👩‍💼" },
                { value: "family", label: "Family", icon: "👨‍👩‍👧" },
                { value: "kids", label: "Kids", icon: "🧒" },
                { value: "friends", label: "Friends", icon: "👫" },
                { value: "reporter", label: "Reporter", icon: "🎤" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
              highlighted={ideaAppliedFields.has("presenter")}
              onManualSelect={() => clearIdeaHighlight("presenter")}
            />
            <DropdownField
              label="Family Origin"
              icon="🌍"
              value={familyOrigin}
              onChange={setFamilyOrigin}
              highlighted={ideaAppliedFields.has("familyOrigin")}
              onManualSelect={() => clearIdeaHighlight("familyOrigin")}
              options={[
                { value: "indian", label: "Indian", icon: "🌏" },
                { value: "south-asian", label: "South Asian", icon: "🌏" },
                { value: "east-asian", label: "East Asian", icon: "🌏" },
                {
                  value: "southeast-asian",
                  label: "Southeast Asian",
                  icon: "🌏",
                },
                {
                  value: "middle-eastern",
                  label: "Middle Eastern",
                  icon: "🌍",
                },
                { value: "african", label: "African", icon: "🌍" },
                {
                  value: "caucasian",
                  label: "Caucasian / European",
                  icon: "🌍",
                },
                {
                  value: "latin-american",
                  label: "Latin American / Hispanic",
                  icon: "🌎",
                },
                {
                  value: "north-american",
                  label: "North American",
                  icon: "🌎",
                },
                {
                  value: "pacific-islander",
                  label: "Pacific Islander / Oceanian",
                  icon: "🌎",
                },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
            />

{dialogue === "yes" && <>
<DropdownField
  label="Voice Accent"
  icon="🎙️"
  value={accent}
  onChange={setAccent}
  highlighted={ideaAppliedFields.has("accent")}
  onManualSelect={() => clearIdeaHighlight("accent")}
  options={[
    { value: "south-indian-english", label: "South Indian English", icon: "🗣️" },
    { value: "north-indian-english", label: "North Indian English", icon: "🗣️" },
    { value: "kannada-accent", label: "Kannada Accent", icon: "🗣️" },
    { value: "telugu-accent", label: "Telugu Accent", icon: "🗣️" },
    { value: "tamil-accent", label: "Tamil Accent", icon: "🗣️" },
    { value: "hindi-accent", label: "Hindi Accent", icon: "🗣️" },
    { value: "neutral-indian", label: "Neutral Indian English", icon: "🗣️" },
    { value: "not-sure", label: "I'm not sure", icon: "❓" },
  ]}
/>
            <div className="col-span-2 sm:col-span-1">
              <MultiSelectDropdown
                label="Language(s)"
                icon="🌐"
                selected={languages}
                forceOpen={openLanguageDropdown}
                onForceOpenConsumed={() => setOpenLanguageDropdown(false)}
                highlighted={ideaAppliedFields.has("languages")}
                onManualSelect={() => clearIdeaHighlight("languages")}
                onChange={(newLangs) => {
                  setLanguages(newLangs);
                  // Show popup only the first time a language is selected
                  if (
                    newLangs.length === 1 &&
                    languages.length === 0 &&
                    !lipSyncPopupShown
                  ) {
                    setShowLipSyncPopup(true);
                    setLipSyncPopupShown(true);
                  }
                }}
                options={[
                  "Hindi",
                  "English",
                  "Bengali",
                  "Telugu",
                  "Marathi",
                  "Tamil",
                  "Gujarati",
                  "Urdu",
                  "Kannada",
                  "Malayalam",
                  "Odia",
                  "Punjabi",
                  "Assamese",
                  "Maithili",
                  "Sanskrit",
                  "Konkani",
                  "Nepali",
                  "Sindhi",
                  "Dogri",
                  "Kashmiri",
                  "Manipuri",
                  "Bodo",
                  "Santali",
                  "I'm not sure",
                ]}
              />
            </div>
            </>}
          </div>

          {/* ── PRODUCT & MODEL UPLOADS (inside Presenter card) ── */}
          <div className="mt-1 pt-1">
            <div className="flex items-center gap-6 flex-wrap">
              {/* ── Reference Image Upload ── */}
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold text-gray-600 flex items-center gap-1 flex-shrink-0">
                  🖼️ Reference Image
                </p>
                {uploadingAssets.referenceImage ? (
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-dashed border-blue-300 bg-blue-50">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : referenceImagePreview ? (
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setReferenceImagePreview(null);
                        setReferenceImageUrl(null);
                      }}
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-[9px] font-bold shadow z-10"
                      aria-label="Remove reference image"
                    >
                      ✕
                    </button>
                    <img
                      src={referenceImagePreview}
                      alt="Reference preview"
                      className="w-10 h-10 rounded-lg object-cover border border-blue-300"
                    />
                  </div>
                ) : (
                  <label className="w-10 h-10 flex flex-col items-center justify-center border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all flex-shrink-0 gap-0.5">
                    <svg
                      className="w-3.5 h-3.5 text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        handleReferenceImageUpload(e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>

              {/* ── Reference Video Upload ── */}
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold text-gray-600 flex items-center gap-1 flex-shrink-0">
                  🎬 Reference Video
                </p>
                {uploadingAssets.referenceVideo ? (
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-dashed border-purple-300 bg-purple-50">
                    <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : referenceVideoPreview ? (
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setReferenceVideoPreview(null);
                        setReferenceVideoUrl(null);
                        setReferenceVideoLink("");
                      }}
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-[9px] font-bold shadow z-10"
                      aria-label="Remove reference video"
                    >
                      ✕
                    </button>
                    <video
                      src={referenceVideoPreview}
                      className="w-10 h-10 rounded-lg object-cover border border-purple-300"
                    />
                  </div>
                ) : (
                  <label className="w-10 h-10 flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all flex-shrink-0 gap-0.5">
                    <svg
                      className="w-3.5 h-3.5 text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.618A1 1 0 0121 8.236v7.528a1 1 0 01-1.447.894L15 14v-4zm-9 2h.01M5 7h10a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2z" />
                    </svg>
                    <input
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => {
                        handleReferenceVideoUpload(e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
                <input
                  type="url"
                  value={referenceVideoLink}
                  onChange={(e) => {
                    setReferenceVideoLink(e.target.value);
                    if (e.target.value.trim()) {
                      setReferenceVideoUrl(e.target.value.trim());
                    }
                  }}
                  placeholder="Video URL"
                  className="w-28 h-8 border border-purple-200 rounded-lg px-2 text-[10px] focus:outline-none focus:ring-2 focus:ring-purple-200"
                />
              </div>

              {/* ── Product Upload ── */}
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold text-gray-600 flex items-center gap-1 flex-shrink-0">
                  📦 Product Image
                  <span className="relative group ml-1">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </button>
                    <div className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg p-3 hidden group-hover:block">
                      <div className="text-xs text-gray-500">
                        Upload your product image — AI will place it naturally
                        within the scene.
                      </div>
                      <div className="text-xs text-blue-600 font-medium mt-1">
                        💡 Clear background works best
                      </div>
                    </div>
                  </span>
                </p>
                <div className="flex gap-1.5 flex-wrap items-center">
                  {productPreviews.map((preview, i) => (
                    <div key={`pp-${i}`} className="relative flex-shrink-0">
                      {uploadingAssets.product && i === productPreviews.length - 1 ? (
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setProductPreviews((p) =>
                                p.filter((_, idx) => idx !== i),
                              );
                              setProductUploadedUrls((p) =>
                                p.filter((_, idx) => idx !== i),
                              );
                            }}
                            className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-[9px] font-bold shadow z-10"
                          >
                            ✕
                          </button>
                          <img
                            src={preview}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                          />
                        </>
                      )}
                    </div>
                  ))}
                  {totalSubjects < 3 && !uploadingAssets.product && (
                    <label className="w-10 h-10 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-blue-100 transition-all flex-shrink-0 gap-0.5">
                      <svg
                        className="w-3.5 h-3.5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          handleProductUpload(e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* ── Model Upload ── */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-bold text-gray-600 flex items-center gap-1 flex-shrink-0">
                    👤 Model Image
                    <span className="relative group ml-1">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.5}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                      <div className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg p-3 hidden group-hover:block">
                        <div className="text-xs text-gray-500">
                          Upload a person — the AI will use that exact person as
                          your video presenter.
                        </div>
                        <div className="text-xs text-blue-600 font-medium mt-1">
                          💡 Full-body photo for best results
                        </div>
                      </div>
                    </span>
                  </p>
                  {/* Hidden file input — opened programmatically after walk question answered */}
                  <input
                    ref={(el) => {
                      if (el) el._modelInput = true;
                    }}
                    id="model-file-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      handleModelUpload(f);
                      e.target.value = "";
                    }}
                  />
                  <div className="flex gap-1.5 flex-wrap items-center">
                    {modelPreviews.map((preview, i) => (
                      <div key={`mp-${i}`} className="relative flex-shrink-0">
                        {uploadingAssets.model && i === modelPreviews.length - 1 ? (
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setModelPreviews((p) =>
                                  p.filter((_, idx) => idx !== i),
                                );
                                setModelUploadedUrls((p) =>
                                  p.filter((_, idx) => idx !== i),
                                );
                                setWalkingInlineAnswer(null);
                                setModelWalking(false);
                              }}
                              className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-[9px] font-bold shadow z-10"
                            >
                              ✕
                            </button>
                            <img
                              src={preview}
                              alt=""
                              className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                            />
                          </>
                        )}
                      </div>
                    ))}
                    {totalSubjects < 3 && !uploadingAssets.model && (
                      <button
                        type="button"
                        onClick={() => {
                          setWalkingInlineAnswer(null);
                          setModelWalking(false);
                          setShowWalkingInlineMsg(true);
                        }}
                        className="w-10 h-10 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-blue-100 transition-all flex-shrink-0 gap-0.5"
                      >
                        <svg
                          className="w-3.5 h-3.5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                {/* Walking question — shown when upload button clicked, BEFORE file picker */}
                {showWalkingInlineMsg && walkingInlineAnswer === null && (
                  <div className="flex items-center gap-2 px-2.5 py-1.5 bg-blue-50 border border-blue-100 rounded-lg">
                    <span className="text-xs text-blue-800 font-medium">
                      🚶 Do you want to walk in the video? Upload a full body
                      image
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setWalkingInlineAnswer(true);
                        setModelWalking(true);
                        document.getElementById("model-file-input")?.click();
                      }}
                      className="px-2.5 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 transition-colors flex-shrink-0"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setWalkingInlineAnswer(false);
                        setModelWalking(false);
                        setShowWalkingInlineMsg(false);
                        document.getElementById("model-file-input")?.click();
                      }}
                      className="px-2.5 py-0.5 bg-gray-200 text-gray-700 text-xs font-semibold rounded-md hover:bg-gray-300 transition-colors flex-shrink-0"
                    >
                      No
                    </button>
                  </div>
                )}
                {/* Full body reminder persists after Yes + upload */}
                {walkingInlineAnswer === true && modelPreviews.length > 0 && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-100 rounded-lg">
                    <span className="text-xs text-amber-800 font-medium">
                      📸 Full-body image uploaded{" "}
                    </span>
                  </div>
                )}
              </div>
              {/* ── Thumbnail Checkbox ── */}
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={generateThumbnails}
                    onChange={(e) => setGenerateThumbnails(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer"
                  />
                  <p className="text-xs font-bold text-gray-600">🖼️ Thumbnails</p>
                </label>
              </div>
              {/* ── Hook Line Input ── */}
              {generateThumbnails && (
                <div className="flex items-center gap-2 w-full">
                  <p className="text-xs font-bold text-gray-600 flex-shrink-0">🪝 Hook Line</p>
                  <input
                    type="text"
                    value={hookLine}
                    onChange={(e) => setHookLine(e.target.value)}
                    placeholder="e.g. This changed everything (leave blank for AI)"
                    maxLength={60}
                    className="flex-1 h-8 border border-gray-200 rounded-lg px-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              )}
            </div>
            {totalSubjects >= 3 && (
              <span className="mt-1.5 inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-100">
                ✋ Max 3 images reached
              </span>
            )}
          </div>
        </div>

        {/* ── VISUAL SETTINGS ── */}
        <div className="bg-pink-50 rounded-2xl border border-pink-100 p-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
<DropdownField
              label="Background"
              icon="🏙️"
              value={background}
              onChange={setBackground}
              highlighted={ideaAppliedFields.has("background")}
              onManualSelect={() => clearIdeaHighlight("background")}
              options={[
                { value: "modern-office", label: "Modern Office", icon: "🏢" },
                { value: "studio", label: "Studio", icon: "🎥" },
                { value: "outdoor", label: "Outdoor", icon: "🌆" },
                { value: "home", label: "Home", icon: "🏠" },
                { value: "nature", label: "Nature", icon: "🌳" },
                { value: "urban", label: "Urban", icon: "🏙️" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
            />
            <DropdownField
              label="Season / Weather"
              icon="🌤️"
              value={season}
              onChange={setSeason}
              highlighted={ideaAppliedFields.has("season")}
              onManualSelect={() => clearIdeaHighlight("season")}
              options={[
                { value: "sunny", label: "Sunny", icon: "☀️" },
                { value: "cloudy", label: "Cloudy", icon: "☁️" },
                { value: "rainy", label: "Rainy", icon: "🌧️" },
                { value: "snowy", label: "Snowy", icon: "❄️" },
                { value: "autumn", label: "Autumn", icon: "🍂" },
                { value: "spring", label: "Spring", icon: "🌿" },
                { value: "summer", label: "Summer", icon: "🌞" },
                { value: "winter", label: "Winter", icon: "🌨️" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
            />
            <DropdownField
              label="Camera Shot"
              icon="📷"
              value={cameraShot}
              onChange={setCameraShot}
              highlighted={ideaAppliedFields.has("cameraShot")}
              onManualSelect={() => clearIdeaHighlight("cameraShot")}
              options={[
                { value: "close-up", label: "Close-up", icon: "🔍" },
                { value: "medium", label: "Medium", icon: "📷" },
                { value: "full-body", label: "Full Body", icon: "🧍" },
                { value: "wide-shot", label: "Wide Shot", icon: "🌅" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
            />
            <DropdownField
              label="Camera Motion"
              icon="🎞️"
              value={cameraMotion}
              onChange={setCameraMotion}
              highlighted={ideaAppliedFields.has("cameraMotion")}
              onManualSelect={() => clearIdeaHighlight("cameraMotion")}
              options={[
                { value: "static", label: "Static", icon: "🎞️" },
                { value: "pan-left", label: "Pan Left", icon: "⬅️" },
                { value: "pan-right", label: "Pan Right", icon: "➡️" },
                { value: "zoom-in", label: "Zoom In", icon: "🔍" },
                { value: "zoom-out", label: "Zoom Out", icon: "🔎" },
                { value: "tilt-up", label: "Tilt Up", icon: "⬆️" },
                { value: "tilt-down", label: "Tilt Down", icon: "⬇️" },
                { value: "dynamic", label: "Dynamic", icon: "🎬" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
            />

            {/* ── BACKGROUND IMAGE UPLOAD (label + upload box side by side) ── */}
            <div>
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold text-gray-600 flex items-center gap-1 flex-shrink-0">
                  🏞️ Background Image
                  <span className="relative group ml-1">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </button>
                    <div className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg p-3 hidden group-hover:block">
                      <div className="text-xs text-gray-500">
                        Upload a custom background — overrides the Background
                        dropdown. AI uses it as the scene backdrop.
                      </div>
                      <div className="text-xs text-blue-600 font-medium flex items-center gap-1 mt-1">
                        <span>💡</span>
                        <span>High-res landscape works best</span>
                      </div>
                    </div>
                  </span>
                </p>
                <div className="flex gap-1.5 flex-wrap items-center">
                  {backgroundPreviews.map((preview, i) => (
                    <div
                      key={`vs-bg-${i}`}
                      className="relative inline-flex flex-shrink-0"
                    >
                      {uploadingAssets.background && i === backgroundPreviews.length - 1 ? (
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setBackgroundPreviews((p) =>
                                p.filter((_, idx) => idx !== i),
                              );
                              setBackgroundUploadedUrls((p) =>
                                p.filter((_, idx) => idx !== i),
                              );
                            }}
                            className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-[9px] font-bold shadow z-10 transition-colors"
                          >
                            ✕
                          </button>
                          <img
                            src={preview}
                            alt={`bg ${i + 1}`}
                            className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                          />
                        </>
                      )}
                    </div>
                  ))}
                  {totalSubjects < 3 && !uploadingAssets.background && (
                    <label className="w-10 h-10 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-blue-100 transition-all flex-shrink-0 gap-0.5">
                      <svg
                        className="w-3.5 h-3.5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          handleBackgroundUpload(e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BUSINESS SETTINGS ── */}
        <div className="bg-pink-50 rounded-2xl border border-pink-100 p-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
<DropdownField
              label="Industry"
              icon="🏭"
              value={industry}
              onChange={setIndustry}
              highlighted={ideaAppliedFields.has("industry")}
              onManualSelect={() => clearIdeaHighlight("industry")}
              options={[
                { value: "technology", label: "Technology", icon: "💻" },
                { value: "healthcare", label: "Healthcare", icon: "🏥" },
                { value: "education", label: "Education", icon: "🎓" },
                { value: "finance", label: "Finance", icon: "📈" },
                { value: "retail", label: "Retail", icon: "🛒" },
                { value: "real-estate", label: "Real Estate", icon: "🏠" },
                { value: "automotive", label: "Automotive", icon: "🚗" },
                {
                  value: "food-beverage",
                  label: "Food & Beverage",
                  icon: "🍽️",
                },
                { value: "entertainment", label: "Entertainment", icon: "▶️" },
                { value: "fitness", label: "Fitness", icon: "🏋️" },
                { value: "travel", label: "Travel", icon: "🌏" },
                { value: "fashion", label: "Fashion", icon: "👗" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
            />
           <DropdownField
              label="Offers"
              icon="🏷️"
              value={offer}
              onChange={setOffer}
              highlighted={ideaAppliedFields.has("offer")}
              onManualSelect={() => clearIdeaHighlight("offer")}
              options={[
                { value: "50-off", label: "50% Off", icon: "💸" },
                { value: "bogo", label: "Buy One Get One", icon: "🎁" },
                { value: "free-shipping", label: "Free Shipping", icon: "🚚" },
                { value: "limited-time", label: "Limited Time", icon: "⌛" },
                { value: "new-customer", label: "New Customer", icon: "👋" },
                { value: "bundle-deal", label: "Bundle Deal", icon: "📦" },
                { value: "seasonal-sale", label: "Seasonal Sale", icon: "📅" },
                { value: "clearance", label: "Clearance", icon: "🏷️" },
                { value: "no-offer", label: "No Offer", icon: "✖️" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
            />
            <DropdownField
              label="Logo Position"
              icon="🖼️"
              value={logoPosition}
              onChange={setLogoPosition}
              highlighted={ideaAppliedFields.has("logoPosition")}
              onManualSelect={() => clearIdeaHighlight("logoPosition")}
              options={[
                { value: "top-left", label: "Top Left Corner", icon: "↖️" },
                { value: "top-right", label: "Top Right Corner", icon: "↗️" },
                {
                  value: "bottom-left",
                  label: "Bottom Left Corner",
                  icon: "↙️",
                },
                {
                  value: "bottom-right",
                  label: "Bottom Right Corner",
                  icon: "↘️",
                },
                { value: "center", label: "Center", icon: "⊙" },
                { value: "no-logo", label: "No Logo", icon: "✖️" },
                { value: "not-sure", label: "I'm not sure", icon: "❓" },
              ]}
            />
          </div>

          {/* ── COMPANY NAME + LOGO UPLOAD ── */}
          <div className="mt-2 pt-2 border-t border-pink-100">
            {/* Status banner */}
            {clientProfileLoading && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl">
                <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                <span className="text-xs text-blue-600 font-medium">
                  Loading your company profile…
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {/* Company Name — from businessInfo.name */}
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1 flex-wrap">
                  🏢 Company Name
                </p>
                <div className="relative">
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Your brand name"
                    className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  {clientProfileLoading && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              {/* Logo Upload — from businessInfo.logoImage */}
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold text-gray-600 flex items-center gap-1 flex-shrink-0 flex-wrap">
                  🖼️ Company Logo
                  <span className="relative group">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </button>
                    <div className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg p-3 hidden group-hover:block">
                      <div className="text-xs text-gray-500">
                        Upload your brand logo to appear in the video.
                      </div>
                      <div className="text-xs text-blue-600 font-medium flex items-center gap-1 mt-1">
                        <span>💡</span>
                        <span>PNG with transparent background works best</span>
                      </div>
                    </div>
                  </span>
                </p>
                {clientProfileLoading || uploadingAssets.logo ? (
                  <div className="w-10 h-10 flex items-center justify-center border-2 border-dashed border-blue-200 bg-blue-50/40 rounded-lg flex-shrink-0">
                    <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : logoPreview ? (
                  <div className="relative inline-flex flex-shrink-0 rounded-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setLogoPreview(null);
                        setLogoUploadedUrl(null);
                        setClientProfileLoaded(false);
                      }}
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-[9px] font-bold shadow z-10 transition-colors"
                    >
                      ✕
                    </button>
                    <img
                      src={logoPreview}
                      alt="logo"
                      className="w-10 h-10 rounded-lg object-contain border border-gray-200 px-1"
                    />
                  </div>
                ) : (
                  <label className="w-10 h-10 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-blue-100 transition-all flex-shrink-0 gap-0.5">
                    <svg
                      className="w-3.5 h-3.5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>
                    <input
                      type="file"
                      accept="image/*,.svg"
                      className="hidden"
                      onChange={(e) => {
                        handleLogoUpload(e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── VOICE SAMPLE ── */}
        {aiModel === "seedance" && <div className="rounded-2xl border p-2 bg-pink-50">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-gray-700 flex items-center gap-1 flex-shrink-0">
              🎤 Voice Sample
              <span className="relative group ml-1">
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <div className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg p-3 hidden group-hover:block">
                  <div className="text-xs text-gray-500">
                    Upload a voice sample — the AI will clone that exact voice
                    and use it to make the model speak in your video.
                  </div>
                  <div className="text-xs text-blue-600 font-medium flex items-center gap-1 mt-1">
                    <span>💡</span>
                    <span>
                      Clear recording with minimal background noise works best
                    </span>
                  </div>
                </div>
              </span>
            </p>
            {voicePreview ? (
              <div className="flex items-center gap-2">
                <span className="text-sm">🎵</span>
                <span className="text-xs text-gray-600 font-medium truncate max-w-[120px]">
                  {voicePreview}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setVoiceFile(null);
                    setVoicePreview(null);
                    setVoiceUploadedUrl(null);
                  }}
                  className="text-xs text-red-400 hover:text-red-600"
                >
                  Remove
                </button>
              </div>
            ) : (
              <label className="w-10 h-10 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-blue-100 transition-all flex-shrink-0 gap-0.5">
                <svg
                  className="w-3.5 h-3.5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                <input
                  type="file"
                  accept=".mp3,.wav,audio/mpeg,audio/wav"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setVoiceFile(file);
                    setVoicePreview(file.name);
                    e.target.value = "";
                    try {
                      const res = await apiService.uploadVoice(file);
                      setVoiceUploadedUrl(res.url);
                    } catch (err) {
                      console.error("Voice upload failed:", err);
                    }
                  }}
                />
              </label>
            )}
          </div>
        </div>}

        {/* ── GENERATE BUTTON ── */}
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canSubmit || isLoading}
          className={`w-full py-3 rounded-2xl text-white font-bold text-base transition-all duration-300 flex items-center justify-center gap-2 shadow-lg ${
            canSubmit && !isLoading
              ? "bg-gradient-to-r from-[#b84040] via-[#9333ea] to-[#b84040] hover:opacity-90 active:scale-95"
              : "bg-gray-300 cursor-not-allowed opacity-60"
          }`}
        >
          {isLoading ? (
            <>
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Generating…</span>
            </>
          ) : (
            <>
              <svg viewBox="0 0 40 40" width="20" height="20" fill="none">
                <polygon points="7,8 22,20 7,32" fill="white" />
                <polygon points="18,8 33,20 18,32" fill="white" fillOpacity="0.6" />
              </svg>
              <span>Generate Video</span>
            </>
          )}
        </button>
      </div>

      {/* ── RIGHT PANEL ── */}
      {hasGenerated && <div className="w-full lg:w-[35%] flex-shrink-0">
        <div className="sticky top-0 self-start max-h-[calc(100vh-4rem)] overflow-y-auto custom-scrollbar">
          {sceneWorkflow ? (
            <div
              className="bg-teal-50 rounded-2xl border border-gray-200 shadow-sm flex flex-col"
              style={{
                maxHeight: "calc(100vh - 120px)",
                minHeight: "480px",
                overflowY: "auto",
              }}
            >
              <div className="h-1 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 flex-shrink-0" />
              {sceneWorkflow}
            </div>
          ) : (
            /* ── Default placeholder ── */
            <div className="bg-teal-50 rounded-2xl border border-gray-200 overflow-hidden">
              {/* Decorative top bar */}
              <div className="h-1 w-full bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400" />

              <div className="p-4 flex flex-col items-center justify-start min-h-[360px] text-center">
                {isLoading ? (
                  <div className="flex flex-col items-center space-y-5 w-full">
                    {/* Spinner */}
                    <div className="relative w-20 h-20">
                      <div className="absolute inset-0 border-4 border-blue-100 rounded-full" />
                      <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      <div className="absolute inset-0 flex items-center justify-center text-3xl">
                        🎬
                      </div>
                    </div>

                    <div className="space-y-1.5 w-full">
                      <h3 className="text-lg font-bold text-gray-800">
                        Generating Your Video…
                      </h3>
                      <p className="text-sm text-gray-500">
                        AI is crafting your script. Usually 30–60 seconds.
                      </p>
                    </div>

                    {/* Animated skeleton */}
                    <div className="w-full space-y-2.5 mt-2">
                      {[100, 80, 92, 65, 88].map((w, i) => (
                        <div
                          key={i}
                          className="h-2 bg-gray-100 rounded-full animate-pulse"
                          style={{
                            width: `${w}%`,
                            animationDelay: `${i * 100}ms`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center space-y-4">
                    {/* ── Selection Summary (above icon) ── */}
                    {(prompt ||
                      videoType ||
                      presenter ||
                      familyOrigin ||
                      styleTone ||
                      dialogue ||
                      background ||
                      season ||
                      cameraShot ||
                      cameraMotion ||
                      industry ||
                      offer ||
                      logoPosition ||
                      languages.length > 0 ||
                      companyName ||
                      voicePreview ||
                      productPreviews.length > 0 ||
                      modelPreviews.length > 0 ||
                      backgroundPreviews.length > 0 ||
                      logoPreview) && (
                      <div className="w-full bg-white/70 rounded-xl border border-gray-100 p-3 space-y-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Your Selections
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {prompt && (
                            <span className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full max-w-[180px] truncate">
                              ✏️{" "}
                              {prompt.length > 24
                                ? prompt.slice(0, 24) + "…"
                                : prompt}
                            </span>
                          )}
                          {companyName && (
                            <span className="text-xs bg-gray-50 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full">
                              🏢 {companyName}
                            </span>
                          )}
                          {videoType && videoType !== "not-sure" && (
                            <span className="text-xs bg-violet-50 text-violet-700 border border-violet-100 px-2 py-0.5 rounded-full">
                              🎥 {videoType}
                            </span>
                          )}
                          {duration && (
                            <span className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full">
                              ⏱️ {duration}s
                            </span>
                          )}
                          {aspectRatio && (
                            <span className="text-xs bg-purple-50 text-purple-700 border border-purple-100 px-2 py-0.5 rounded-full">
                              📐 {aspectRatio}
                            </span>
                          )}
                          {scriptMode && (
                            <span className="text-xs bg-green-50 text-green-700 border border-green-100 px-2 py-0.5 rounded-full">
                              {scriptMode === "ai-enhanced"
                                ? "🚀 AI Enhanced"
                                : "⚡ Direct"}
                            </span>
                          )}
                          {presenter && presenter !== "not-sure" && (
                            <span className="text-xs bg-pink-50 text-pink-700 border border-pink-100 px-2 py-0.5 rounded-full">
                              🧑 {presenter}
                            </span>
                          )}
                          {familyOrigin && familyOrigin !== "not-sure" && (
                            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-full">
                              🌍 {familyOrigin}
                            </span>
                          )}
                          {styleTone && styleTone !== "not-sure" && (
                            <span className="text-xs bg-teal-50 text-teal-700 border border-teal-100 px-2 py-0.5 rounded-full">
                              🎨 {styleTone}
                            </span>
                          )}
                          {dialogue && (
                            <span className="text-xs bg-cyan-50 text-cyan-700 border border-cyan-100 px-2 py-0.5 rounded-full">
                              🗣️ Dialogue: {dialogue}
                            </span>
                          )}
                          {background && background !== "not-sure" && (
                            <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-full">
                              🏙️ {background}
                            </span>
                          )}
                          {season && season !== "not-sure" && (
                            <span className="text-xs bg-yellow-50 text-yellow-700 border border-yellow-100 px-2 py-0.5 rounded-full">
                              🌤️ {season}
                            </span>
                          )}
                          {cameraShot && cameraShot !== "not-sure" && (
                            <span className="text-xs bg-slate-50 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-full">
                              📷 {cameraShot}
                            </span>
                          )}
                          {cameraMotion && cameraMotion !== "not-sure" && (
                            <span className="text-xs bg-slate-50 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full">
                              🎞️ {cameraMotion}
                            </span>
                          )}
                          {industry && industry !== "not-sure" && (
                            <span className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-full">
                              🏭 {industry}
                            </span>
                          )}
                          {offer &&
                            offer !== "not-sure" &&
                            offer !== "no-offer" && (
                              <span className="text-xs bg-red-50 text-red-600 border border-red-100 px-2 py-0.5 rounded-full">
                                🏷️ {offer}
                              </span>
                            )}
                          {logoPosition &&
                            logoPosition !== "not-sure" &&
                            logoPosition !== "no-logo" && (
                              <span className="text-xs bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded-full">
                                🖼️ Logo: {logoPosition}
                              </span>
                            )}
                          {languages.length > 0 && (
                            <span className="text-xs bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded-full">
                              🌐 {languages.length} language
                              {languages.length > 1 ? "s" : ""}
                            </span>
                          )}
                          {logoPreview && (
                            <span className="text-xs bg-gray-50 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full">
                              🖼️ Logo uploaded
                            </span>
                          )}
                          {productPreviews.length > 0 && (
                            <span className="text-xs bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded-full">
                              📦 {productPreviews.length} product
                              {productPreviews.length > 1 ? "s" : ""}
                            </span>
                          )}
                          {modelPreviews.length > 0 && (
                            <span className="text-xs bg-purple-50 text-purple-600 border border-purple-100 px-2 py-0.5 rounded-full">
                              👤 {modelPreviews.length} model
                              {modelPreviews.length > 1 ? "s" : ""}
                            </span>
                          )}
                          {backgroundPreviews.length > 0 && (
                            <span className="text-xs bg-green-50 text-green-600 border border-green-100 px-2 py-0.5 rounded-full">
                              🏞️ {backgroundPreviews.length} bg image
                              {backgroundPreviews.length > 1 ? "s" : ""}
                            </span>
                          )}
                          {voicePreview && (
                            <span className="text-xs bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100 px-2 py-0.5 rounded-full">
                              🎙️ Voice uploaded
                            </span>
                          )}
                          {aiModel && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full border ${
                                aiModel === "veo"
                                  ? "bg-purple-50 text-purple-700 border-purple-100"
                                  : "bg-green-50 text-green-700 border-green-100"
                              }`}
                            >
                              {aiModel === "veo" ? "🎬 Veo" : "⚡ Seedance"}
                            </span>
                          )}
                          <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={generateThumbnails}
                              onChange={(e) => setGenerateThumbnails(e.target.checked)}
                              className="w-3 h-3 rounded border-gray-300 cursor-pointer"
                            />
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${
                              generateThumbnails
                                ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                : "bg-gray-50 text-gray-400 border-gray-200"
                            }`}>🖼️ Thumbnails</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Icon */}
                    <div
                      className="w-10 h-10 bg-gradient-to-br from-indigo-50 to-blue-100 rounded-xl flex items-center justify-center border border-indigo-100"
                      style={{ marginTop: "70px" }}
                    >
                      <span className="text-xl">🎬</span>
                    </div>

                    <div className="space-y-1.5">
                      <h3 className="text-s font-bold text-gray-800">
                        Your Video Awaits...
                      </h3>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>}

      {/* ── No Logo Confirmation Modal ── */}
      {showLogoConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-auto overflow-hidden">
            <div className="bg-amber-500 px-5 py-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⚠️</span>
                <h2 className="text-white font-bold text-base">
                  No Logo Uploaded
                </h2>
              </div>
              <p className="text-white/80 text-xs mt-1">
                Your brand logo won't appear in the video
              </p>
            </div>
            <div className="px-5 py-5 space-y-4">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-3">
                <span className="text-2xl flex-shrink-0">🤖</span>
                <p className="text-sm text-amber-800 leading-relaxed">
                  Without a logo, the AI will generate the video{" "}
                  <strong>without your brand identity</strong>. This may reduce
                  brand recognition.
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-3">
                <span className="text-xl flex-shrink-0">💡</span>
                <p className="text-sm text-blue-700 leading-relaxed">
                  For best results, upload a{" "}
                  <strong>PNG logo with transparent background</strong>.
                </p>
              </div>
              <p className="text-sm text-gray-600 text-center font-medium">
                Are you sure you want to continue without a logo?
              </p>
            </div>
            <div className="px-5 pb-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowLogoConfirmModal(false);
                  document
                    .getElementById("image-upload-section")
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className="w-full py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
              >
                🖼️ Upload Logo
              </button>
              <button
                type="button"
                onClick={() => {
                  setSkipLogo(true);
                  setShowLogoConfirmModal(false);
                  // Continue to next check
                  if (productUploadedUrls.length === 0 && !skipProduct) {
                    setShowProductConfirmModal(true);
                  } else if (modelUploadedUrls.length === 0 && !skipModel) {
                    setShowModelConfirmModal(true);
                  } else {
                    runGenerate();
                  }
                }}
                className="w-full py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                Yes, Continue Without Logo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── No Product Confirmation Modal ── */}
      {showProductConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-auto overflow-hidden">
            <div className="bg-amber-500 px-5 py-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⚠️</span>
                <h2 className="text-white font-bold text-base">
                  No Product Image Uploaded
                </h2>
              </div>
              <p className="text-white/80 text-xs mt-1">
                AI will generate the video without a product image
              </p>
            </div>
            <div className="px-5 py-5 space-y-4">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-3">
                <span className="text-2xl flex-shrink-0">🤖</span>
                <p className="text-sm text-amber-800 leading-relaxed">
                  Without a product image, the AI will{" "}
                  <strong>generate a generic product scene</strong>. Your actual
                  product won't appear in the video.
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-3">
                <span className="text-xl flex-shrink-0">💡</span>
                <p className="text-sm text-blue-700 leading-relaxed">
                  For best results, upload a{" "}
                  <strong>clear product photo</strong> with a clean background.
                </p>
              </div>
              <p className="text-sm text-gray-600 text-center font-medium">
                Are you sure you want to continue without a product image?
              </p>
            </div>
            <div className="px-5 pb-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowProductConfirmModal(false);
                  document
                    .getElementById("image-upload-section")
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className="w-full py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
              >
                📦 Upload Product Image
              </button>
              <button
                type="button"
                onClick={() => {
                  setSkipProduct(true);
                  setShowProductConfirmModal(false);
                  if (modelUploadedUrls.length === 0 && !skipModel) {
                    setShowModelConfirmModal(true);
                  } else {
                    runGenerate();
                  }
                }}
                className="w-full py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                Yes, AI Generate Product Scene
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── No Model Confirmation Modal ── */}
      {showModelConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-auto overflow-hidden">
            <div className="bg-amber-500 px-5 py-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⚠️</span>
                <h2 className="text-white font-bold text-base">
                  No Model Image Uploaded
                </h2>
              </div>
              <p className="text-white/80 text-xs mt-1">
                A presenter face will be auto-generated by AI
              </p>
            </div>
            <div className="px-5 py-5 space-y-4">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-3">
                <span className="text-2xl flex-shrink-0">🤖</span>
                <p className="text-sm text-amber-800 leading-relaxed">
                  Without a model image, the AI will{" "}
                  <strong>randomly generate a face</strong> for your video
                  presenter. This may not match your brand or expectations.
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-3">
                <span className="text-xl flex-shrink-0">💡</span>
                <p className="text-sm text-blue-700 leading-relaxed">
                  For best results, upload a <strong>full-body photo</strong> of
                  your preferred model or presenter.
                </p>
              </div>
              <p className="text-sm text-gray-600 text-center font-medium">
                Are you sure you want to continue without uploading a model
                image?
              </p>
            </div>
            <div className="px-5 pb-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowModelConfirmModal(false);
                  document
                    .getElementById("image-upload-section")
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className="w-full py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
              >
                👤 Upload Model Image
              </button>
              <button
                type="button"
                onClick={() => {
                  setSkipModel(true);
                  setShowModelConfirmModal(false);
                  runGenerate();
                }}
                className="w-full py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                Yes, Auto-Generate Face
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── No Image Uploaded — BLOCKING Modal ── */}
{showNoImageModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
    <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-auto overflow-hidden">
      <div className="bg-red-500 px-5 py-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🚫</span>
          <h2 className="text-white font-bold text-base">
            Image Required
          </h2>
        </div>
        <p className="text-white/80 text-xs mt-1">
          At least one image must be uploaded to generate a video
        </p>
      </div>
      <div className="px-5 py-5 space-y-4">
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-3">
          <span className="text-2xl flex-shrink-0">🖼️</span>
          <p className="text-sm text-red-800 leading-relaxed">
            Please upload at least one of:{" "}
            <strong>Product Image</strong>,{" "}
            <strong>Model Image</strong>,{" "}
            <strong>Background Image</strong>, or{" "}
            <strong>Logo</strong> to continue.
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-3">
          <span className="text-xl flex-shrink-0">💡</span>
          <p className="text-sm text-blue-700 leading-relaxed">
            A product or model image gives the AI the best context to
            create a relevant, on-brand video.
          </p>
        </div>
      </div>
      <div className="px-5 pb-5">
        <button
          type="button"
          onClick={() => {
            setShowNoImageModal(false);
            document
              .getElementById("image-upload-section")
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
          className="w-full py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
        >
          🖼️ Upload an Image
        </button>
      </div>
    </div>
  </div>
)}

     {/* ── Ideas Modal ── */}
      {showIdeasModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowIdeasModal(false); }}
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">💡 Choose an Idea</h3>
              <button onClick={() => setShowIdeasModal(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-3">
              {isLoadingIdeas && (
                <div className="flex items-center justify-center py-10">
                  <div className="w-8 h-8 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                </div>
              )}

              {!isLoadingIdeas && ideasError && (
                <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
                  {ideasError}
                </div>
              )}

              {!isLoadingIdeas && !ideasError && ideas.length === 0 && (
                <div className="text-gray-500 text-sm text-center py-10">No ideas found.</div>
              )}

{!isLoadingIdeas && ideas.map((idea) => (
  <div
    key={idea._id}
    onClick={() => applyIdea(idea)}
    className="w-full text-left p-4 border border-gray-200 rounded-xl cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
  >
    <div className="font-semibold text-gray-800 text-sm mb-1">{idea.title}</div>
    <div className="text-xs text-gray-500 mb-2 line-clamp-2">{idea.visualConcept}</div>
    <div className="flex flex-wrap gap-1.5">
      {idea.hashtags?.slice(0, 3).map((tag) => (
        <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>
      ))}
      {idea.videoSettings?.videoType && (
        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">🎬 {idea.videoSettings.videoType}</span>
      )}
    </div>
  </div>
))}
            </div>

            {/* Generate more ideas footer — only enabled when 5 or fewer ideas are available */}
            {/* <div className="border-t border-gray-200 p-4 space-y-2">
              {generateIdeasError && (
                <div className="text-red-600 text-xs bg-red-50 border border-red-200 rounded-lg p-2">
                  {generateIdeasError}
                </div>
              )}
              <button
                type="button"
                onClick={generateNewIdeas}
                disabled={isLoadingIdeas || ideas.length > 5 || isGeneratingIdeas}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isLoadingIdeas || ideas.length > 5 || isGeneratingIdeas
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                }`}
              >
                {isGeneratingIdeas ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Generating New Ideas…</span>
                  </>
                ) : (
                  <>
                    <span>✨</span>
                    <span>Generate Ideas</span>
                  </>
                )}
              </button>
              {!isLoadingIdeas && ideas.length > 5 && (
                <p className="text-xs text-gray-400 text-center">
                  Available when 5 or fewer ideas remain ({ideas.length} currently shown)
                </p>
              )}
            </div> */}
          </div>
        </div>
      )}
      {/* ── Lip Sync Info Popup ── */}
      {showLipSyncPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 overflow-y-auto">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full mx-auto flex flex-col my-auto" style={{ maxWidth: '360px', maxHeight: 'calc(100vh - 24px)', minHeight: 0 }}>
            <div className="bg-primary px-4 py-3 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-xl">🌐</span>
                <h2 className="text-white font-bold text-sm">
                  Multi-Language Video
                </h2>
              </div>
              <p className="text-white/80 text-xs mt-0.5">
                Here's how your video will be generated
              </p>
            </div>
            <div className="px-4 py-3 space-y-2.5 overflow-y-auto flex-1 custom-scrollbar">
              <p className="text-xs text-gray-500">
                Create one video and automatically generate versions in multiple languages.
              </p>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                How it works
              </p>
              <div className="flex items-start gap-2.5">
                <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  1
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-800">🎬 Primary Video Created</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    The <span className="font-semibold text-primary">first language</span> you choose generates the full video with visuals and voice.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  2
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-800">🔊 Additional Languages Added</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Same video reused with a dubbed voice track. Lip movements remain from the primary language (no lip sync).
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 space-y-1.5">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Example</p>
                <div className="flex items-center justify-between">
                  <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                    🎬 {languages[0] || "Hindi"}
                  </span>
                  <span className="text-xs text-gray-400">Full video generated</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full border border-blue-200">
                    🔊 English
                  </span>
                  <span className="text-xs text-gray-400">Dubbed audio (lip sync unchanged)</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center">💡 Only one video is generated. Other languages reuse it.</p>
            </div>
            <div className="px-4 pb-4 flex-shrink-0">
              <button
                type="button"
                className="w-full py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-hover transition-colors"
                onClick={() => {
                  setShowLipSyncPopup(false);
                  setOpenLanguageDropdown(true);
                }}
              >
                Got it, choose languages →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sticky Circular Generate Video FAB ── */}
      {!isLoading && !sceneWorkflow && (
        <div className="fixed top-1/2 left-[65%] -translate-x-1/2 -translate-y-1/2 z-40 flex flex-col items-center hidden">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!canSubmit || isLoading}
            title="Generate Video"
            className={`relative flex items-center justify-center rounded-full shadow-2xl transition-all duration-300 focus:outline-none group ${
              canSubmit && !isLoading
                ? "hover:scale-110 active:scale-95"
                : "opacity-60 cursor-not-allowed"
            }`}
            style={{ width: 100, height: 100 }}
          >
            {/* Outer white ring */}
            <span
              className="absolute inset-0 rounded-full bg-white shadow-lg"
              style={{ boxShadow: "0 6px 30px 0 rgba(0,0,0,0.25)" }}
            />
            {/* Vibrant arc label using SVG with gradient */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <path id="fab-arc" d="M 15,50 A 35,35 0 1,1 85,50" />
                <linearGradient
                  id="textGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop
                    offset="0%"
                    style={{ stopColor: "#2a8a8a", stopOpacity: 1 }}
                  />
                  <stop
                    offset="50%"
                    style={{ stopColor: "#9333ea", stopOpacity: 1 }}
                  />
                  <stop
                    offset="100%"
                    style={{ stopColor: "#2a8a8a", stopOpacity: 1 }}
                  />
                </linearGradient>
              </defs>
              <text
                fontSize="11"
                fontWeight="800"
                fill="url(#textGradient)"
                fontFamily="sans-serif"
                letterSpacing="0.8"
              >
                <textPath href="#fab-arc" startOffset="8%">
                  Generate Video
                </textPath>
              </text>
            </svg>
            {/* Gradient inner circle with play icon */}
            <span
              className="relative z-10 flex items-center justify-center rounded-full"
              style={{
                width: 62,
                height: 62,
                background:
                  "linear-gradient(135deg, #b84040 0%, #9333ea 50%, #b84040 100%)",
                marginTop: 10,
              }}
            >
              {isLoading ? (
                <span className="w-7 h-7 border-[3px] border-white border-t-transparent rounded-full animate-spin block" />
              ) : (
                /* Double-chevron play icon matching the logo */
                <svg
                  viewBox="0 0 40 40"
                  width="34"
                  height="34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <polygon points="7,8 22,20 7,32" fill="white" />
                  <polygon
                    points="18,8 33,20 18,32"
                    fill="white"
                    fillOpacity="0.6"
                  />
                  <polygon
                    points="18,8 33,20 18,32"
                    fill="none"
                    stroke="white"
                    strokeWidth="1.8"
                  />
                </svg>
              )}
            </span>
            {/* Sparkle dot bottom-right with purple glow */}
            <span
              className="absolute bottom-1.5 right-1.5 w-3 h-3"
              style={{
                background: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                filter: "drop-shadow(0 0 4px rgba(147, 51, 234, 0.6))",
              }}
            >
              <svg viewBox="0 0 10 10" width="12" height="12">
                <path
                  d="M5 0 L5.6 4 L10 5 L5.6 6 L5 10 L4.4 6 L0 5 L4.4 4 Z"
                  fill="#9333ea"
                />
              </svg>
            </span>
          </button>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
      `}</style>
    </div>
  );
};

export default VideoGeneratorLayout;
